import { Box, Button, Paper, Typography, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import type Employee from '@src/Objects/Employee';
import { AlertTriangle, RefreshCw, Target } from 'lucide-react';
import React, { useMemo } from 'react';

interface ExpiringCertificatesAlertProps {
    employee: Employee;
}

function ExpiringCertificatesAlert({ employee }: ExpiringCertificatesAlertProps) {
    const theme = useTheme();

    const expiringCertificates = useMemo(() => {
        const certificates = employee.certificates || [];
        const now = new Date();
        const twoMonthsFromNow = new Date();
        twoMonthsFromNow.setMonth(twoMonthsFromNow.getMonth() + 2);

        return certificates.filter((cert) => {
            if (!cert.endDate) return false;
            return cert.endDate <= twoMonthsFromNow && cert.endDate > now;
        });
    }, [employee.certificates]);

    if (expiringCertificates.length === 0) {
        return null;
    }

    const nextExpiring = expiringCertificates.sort((a, b) => a.endDate!.getTime() - b.endDate!.getTime())[0];

    const getDaysUntilExpiration = (endDate: Date) => {
        const now = new Date();
        const diffTime = endDate.getTime() - now.getTime();
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        return diffDays;
    };

    const daysLeft = getDaysUntilExpiration(nextExpiring.endDate!);

    return (
        <Paper
            elevation={0}
            sx={{
                p: 3,
                borderRadius: 2,
                background: 'transparent',
                border: `1px solid ${alpha(theme.palette.warning.main, 0.3)}`,
                borderLeft: `4px solid ${theme.palette.warning.main}`,
                mb: 3,
                '&:hover': {
                    bgcolor: `${theme.palette.warning.main}08`,
                },
            }}
        >
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 2 }}>
                <AlertTriangle size={20} color={theme.palette.warning.main} />
                <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                        Expiring Soon
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                        <strong>{nextExpiring.title}</strong>
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.8, mb: 2 }}>
                        Expires in: {daysLeft} {daysLeft === 1 ? 'week' : 'weeks'}
                    </Typography>
                </Box>
            </Box>

            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Button
                    variant="contained"
                    size="small"
                    startIcon={<RefreshCw size={16} />}
                    sx={{
                        textTransform: 'none',
                        bgcolor: theme.palette.warning.main,
                        color: 'white',
                        '&:hover': {
                            bgcolor: theme.palette.warning.dark,
                            transform: 'translateY(-1px)',
                            boxShadow: `0 4px 12px ${alpha(theme.palette.warning.main, 0.3)}`,
                        },
                        transition: 'all 0.2s ease',
                    }}
                >
                    Renew Certificate
                </Button>

                <Button
                    variant="outlined"
                    size="small"
                    startIcon={<Target size={16} />}
                    sx={{
                        textTransform: 'none',
                        borderColor: alpha(theme.palette.warning.main, 0.5),
                        color: theme.palette.warning.main,
                        '&:hover': {
                            borderColor: theme.palette.warning.main,
                            bgcolor: alpha(theme.palette.warning.main, 0.08),
                            color: theme.palette.warning.main,
                            transform: 'translateY(-1px)',
                        },
                        transition: 'all 0.2s ease',
                    }}
                >
                    Neues Ziel setzen
                </Button>
            </Box>
        </Paper>
    );
}

export default ExpiringCertificatesAlert;
